import React from 'react';
import { Link } from 'react-router-dom';
import { useSectionBase } from '../../Constant/useActiveLibrary';
import {
  searchMembersByLibrary,
  searchBooksByLibrary,
  getMemberIssueHistoryReport,
  getBookIssueHistoryReport,
  getIssuesReport,
  getIssuesByMemberCategoryReport,
  getOverdueBooksReport,
  getFineCollectionReport,
  getPendingFineReport,
  getDailyIssueReturnReport,
  getMostIssuedBooksReport,
  getMostActiveMembersReport,
  getLostDamagedReport,
  getReservationReportData,
  getMemberCategoryOptions,
  getBookCategoriesByLibrary
} from '../../api/api';

// The one real "Member Category" axis today is memberships.member_source —
// members.member_type is free text with no lookup table, so it is
// intentionally NOT used as a report filter. The list below is only a
// same-render fallback shown until getMemberCategoryOptions (the live DB
// enum, see memberModel.getMemberCategoryOptions) resolves — every filter
// using it also passes optionsLoader: getMemberCategoryOptions, which
// ReportPage swaps in once loaded so this never goes stale in practice.
export const MEMBER_CATEGORY_OPTIONS = [
  { value: 'outsider', label: 'Outsider' },
  { value: 'school_parent', label: 'School Parent' }
];

const ISSUE_STATUS_OPTIONS = [
  { value: 'issued', label: 'Issued' },
  { value: 'returned', label: 'Returned' },
  { value: 'overdue', label: 'Overdue' }
];

const FINE_STATUS_BADGE = {
  unpaid: 'bg-danger',
  partial: 'bg-warning text-dark',
  paid: 'bg-success',
  waived: 'bg-secondary'
};

const FINE_STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'waived', label: 'Waived' }
];

const PENDING_FINE_STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partially Paid' }
];

const RESERVATION_STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'ready_for_pickup', label: 'Ready for Pickup' },
  { value: 'collected', label: 'Collected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' }
];

const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
const dateOnly = (value) => (value ? String(value).slice(0, 10) : '-');
const memberSourceLabel = (value) => (value === 'school_parent' ? 'School Parent' : value === 'outsider' ? 'Outsider' : '-');
const titleCase = (value) => (value ? String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');

const statusBadge = (row) => {
  if (row.issue_status === 'returned') return <span className="badge bg-success">Returned</span>;
  if (row.issue_status === 'issued' && row.due_date && new Date(row.due_date) < new Date()) {
    return <span className="badge bg-danger">Overdue</span>;
  }
  return <span className="badge bg-primary">Issued</span>;
};

const fineBadge = (row) =>
  row.fine_status ? (
    <span className={`badge ${FINE_STATUS_BADGE[row.fine_status] || 'bg-secondary'}`}>{row.fine_status}</span>
  ) : (
    '-'
  );

// Same lost/damaged badge convention as lostDamagedConfig's availability_status
// column below, applied to book_issues.book_return_condition ('good' |
// 'damaged' | 'lost', or null while the copy is still out).
const conditionBadge = (row) => {
  if (!row.book_return_condition) return '-';
  const cls = row.book_return_condition === 'lost' ? 'bg-danger' : row.book_return_condition === 'damaged' ? 'bg-warning text-dark' : 'bg-success';
  return <span className={`badge ${cls}`}>{titleCase(row.book_return_condition)}</span>;
};

const memberEntityPicker = {
  label: 'Member',
  placeholder: 'Search name, mobile or membership no.',
  searchFn: searchMembersByLibrary,
  getOptionValue: (m) => m.id,
  getOptionLabel: (m) => `${m.full_name || m.member_name || 'Member'}${m.mobile ? ` - ${m.mobile}` : ''}`,
  renderOption: (option) => {
    const m = option.item || {};
    return (
      <div>
        <div><strong>{m.full_name || m.member_name}</strong> {m.membership_no && <span className="text-muted">({m.membership_no})</span>}</div>
        <div className="small text-muted">{m.mobile} {m.membership_type ? `· ${memberSourceLabel(m.membership_type)}` : ''}</div>
      </div>
    );
  }
};

const bookEntityPicker = {
  label: 'Book',
  placeholder: 'Search title, author or ISBN',
  searchFn: searchBooksByLibrary,
  getOptionValue: (b) => b.id,
  getOptionLabel: (b) => `${b.title}${b.author_name ? ` - ${b.author_name}` : ''}`,
  renderOption: (option) => {
    const b = option.item || {};
    return (
      <div>
        <div><strong>{b.title}</strong></div>
        <div className="small text-muted">{b.author_name} {b.isbn ? `· ISBN ${b.isbn}` : ''}</div>
      </div>
    );
  }
};

const dateFilters = [
  { key: 'from_date', label: 'From Date', type: 'date' },
  { key: 'to_date', label: 'To Date', type: 'date' }
];

const issueColumns = [
  { key: 'member_name', header: 'Member' },
  { key: 'membership_no', header: 'Membership No.' },
  { key: 'member_source', header: 'Category', render: (r) => memberSourceLabel(r.member_source) },
  { key: 'book_title', header: 'Book' },
  { key: 'accession_no', header: 'Accession No.' },
  { key: 'barcode', header: 'Barcode' },
  { key: 'isbn', header: 'ISBN' },
  { key: 'issue_date', header: 'Issue Date', render: (r) => dateOnly(r.issue_date) },
  { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
  { key: 'return_date', header: 'Return Date', render: (r) => dateOnly(r.return_date) },
  { key: 'renew_count', header: 'Renewals' },
  { key: 'status', header: 'Status', render: statusBadge },
  { key: 'overdue_days', header: 'Overdue Days' },
  { key: 'fine_amount', header: 'Fine Amount', render: (r) => (r.fine_amount != null ? money(r.fine_amount) : '-') },
  { key: 'fine_status', header: 'Fine', render: fineBadge },
  { key: 'book_return_condition', header: 'Condition', render: conditionBadge }
];

const issueExportColumns = [
  { key: 'member_name', header: 'Member' },
  { key: 'membership_no', header: 'Membership No.' },
  { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
  { key: 'book_title', header: 'Book' },
  { key: 'accession_no', header: 'Accession No.' },
  { key: 'isbn', header: 'ISBN' },
  { key: 'issue_date', header: 'Issue Date', render: (r) => dateOnly(r.issue_date) },
  { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
  { key: 'return_date', header: 'Return Date', render: (r) => dateOnly(r.return_date) },
  { key: 'renew_count', header: 'Renewals' },
  { key: 'issue_status', header: 'Status', render: (r) => titleCase(r.issue_status) },
  { key: 'overdue_days', header: 'Overdue Days' },
  { key: 'fine_amount', header: 'Fine Amount', render: (r) => (r.fine_amount != null ? money(r.fine_amount) : '') },
  { key: 'fine_status', header: 'Fine Status', render: (r) => titleCase(r.fine_status) }
];

// 5.1 Member Issue History
export const memberIssueHistoryConfig = {
  key: 'member-issue-history',
  category: 'Circulation',
  title: 'Member Issue History',
  subtitle: 'View complete issue, return, renewal and fine history for a member.',
  icon: 'fas fa-user-clock',
  printTitle: 'Member Issue History Report',
  fileNamePrefix: 'Member_Issue_History',
  entityPicker: memberEntityPicker,
  searchPlaceholder: 'Search book title, accession no...',
  filters: [
    ...dateFilters,
    { key: 'status', label: 'Status', type: 'select', options: ISSUE_STATUS_OPTIONS },
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions }
  ],
  columns: issueColumns,
  exportColumns: issueExportColumns,
  fetchFn: (libraryId, memberId, params) => getMemberIssueHistoryReport(libraryId, memberId, params)
};

// 5.2 Book Issue History
export const bookIssueHistoryConfig = {
  key: 'book-issue-history',
  category: 'Circulation',
  title: 'Book Issue History',
  subtitle: 'Track the complete circulation history of a selected book.',
  icon: 'fas fa-book-reader',
  printTitle: 'Book Issue History Report',
  fileNamePrefix: 'Book_Issue_History',
  entityPicker: bookEntityPicker,
  searchPlaceholder: 'Search member, accession no...',
  filters: [
    ...dateFilters,
    { key: 'status', label: 'Status', type: 'select', options: ISSUE_STATUS_OPTIONS },
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions }
  ],
  columns: issueColumns,
  exportColumns: issueExportColumns,
  fetchFn: (libraryId, bookId, params) => getBookIssueHistoryReport(libraryId, bookId, params)
};

// 5.3 Books Issued During a Date/Time Period
export const issuesReportConfig = {
  key: 'issues-report',
  category: 'Circulation',
  title: 'Books Issued Report',
  subtitle: 'Review books issued within a selected date range.',
  icon: 'fas fa-list-alt',
  printTitle: 'Books Issued Report',
  fileNamePrefix: 'Books_Issued_Report',
  searchPlaceholder: 'Search member, book, accession no...',
  filters: [
    ...dateFilters,
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions },
    { key: 'status', label: 'Status', type: 'select', options: ISSUE_STATUS_OPTIONS }
  ],
  columns: issueColumns,
  exportColumns: issueExportColumns,
  fetchFn: (libraryId, params) => getIssuesReport(libraryId, params),
  renderTotals: (totals) => (
    <>
      <span className="rpt-total-chip">Total Transactions<strong>{totals.total_transactions}</strong></span>
      <span className="rpt-total-chip">Currently Issued<strong>{totals.currently_issued}</strong></span>
      <span className="rpt-total-chip">Returned<strong>{totals.returned}</strong></span>
      <span className="rpt-total-chip">Overdue<strong>{totals.overdue}</strong></span>
    </>
  ),
  renderTotalsText: (totals) =>
    totals ? `Total: ${totals.total_transactions} | Currently Issued: ${totals.currently_issued} | Returned: ${totals.returned} | Overdue: ${totals.overdue}` : ''
};

// Drill-down for a category's Total Issues cell below — jumps into the
// Books Issued Report (issuesReportConfig, key 'issues-report') pre-filtered
// to that same category and the date range currently applied here. Only
// linked for the two real member_source values; the 'unknown' bucket (rows
// with no membership record) has nothing a member_source filter can match,
// so it stays plain text.
const CategoryDrillDownLink = ({ row }) => {
  const sectionBase = useSectionBase();
  if (row.member_source !== 'outsider' && row.member_source !== 'school_parent') {
    return <>{row.total_issues}</>;
  }
  const params = new URLSearchParams();
  params.set('member_source', row.member_source);
  if (row._from_date) params.set('from_date', row._from_date);
  if (row._to_date) params.set('to_date', row._to_date);
  return (
    <Link to={`${sectionBase}/reports/issues-report?${params.toString()}`} title="View issues for this category">
      {row.total_issues}
    </Link>
  );
};

// 5.4 Issues by Member Category
export const issuesByMemberCategoryConfig = {
  key: 'issues-by-member-category',
  category: 'Members',
  title: 'Issues by Member Category',
  subtitle: 'Analyse circulation activity across configured member categories.',
  icon: 'fas fa-layer-group',
  printTitle: 'Issues by Member Category Report',
  fileNamePrefix: 'Issues_By_Member_Category',
  pdfOrientation: 'portrait',
  paginated: false,
  filters: [...dateFilters, { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions }],
  columns: [
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'total_issues', header: 'Total Issues', render: (r) => <CategoryDrillDownLink row={r} /> },
    { key: 'currently_issued', header: 'Currently Issued' },
    { key: 'returned', header: 'Returned' },
    { key: 'overdue', header: 'Overdue' },
    { key: 'distinct_members', header: 'Distinct Members' }
  ],
  exportColumns: [
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'total_issues', header: 'Total Issues' },
    { key: 'currently_issued', header: 'Currently Issued' },
    { key: 'returned', header: 'Returned' },
    { key: 'overdue', header: 'Overdue' },
    { key: 'distinct_members', header: 'Distinct Members' }
  ],
  fetchFn: async (libraryId, params) => {
    const res = await getIssuesByMemberCategoryReport(libraryId, params);
    if (!res || res.success === false) return res;
    // Stash the applied date range on each row so the Total Issues drill-down
    // link (CategoryDrillDownLink above) can carry it into the Books Issued
    // Report — params here is the same object ReportPage built from
    // appliedFilters, so this is exactly "the currently-applied date range".
    const data = (res.data || []).map((row) => ({ ...row, _from_date: params.from_date, _to_date: params.to_date }));
    return { ...res, data };
  }
};

// 5.5 Overdue Books Report
export const overdueBooksConfig = {
  key: 'overdue-books',
  category: 'Circulation',
  title: 'Overdue Books Report',
  subtitle: 'View currently overdue books, overdue days and applicable fines.',
  icon: 'fas fa-exclamation-triangle',
  printTitle: 'Overdue Books Report',
  fileNamePrefix: 'Overdue_Books_Report',
  searchPlaceholder: 'Search member, book, accession no...',
  filters: [
    ...dateFilters,
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions },
    { key: 'min_overdue_days', label: 'Min. Overdue Days', type: 'number' },
    { key: 'fine_status', label: 'Fine Status', type: 'select', options: FINE_STATUS_OPTIONS }
  ],
  columns: [
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_source', header: 'Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'phone', header: 'Contact' },
    { key: 'book_title', header: 'Book' },
    { key: 'accession_no', header: 'Accession No.' },
    { key: 'issue_date', header: 'Issue Date', render: (r) => dateOnly(r.issue_date) },
    { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
    { key: 'overdue_days', header: 'Days Overdue' },
    { key: 'fine_amount', header: 'Fine', render: (r) => (r.fine_amount != null ? money(r.fine_amount) : '-') },
    { key: 'fine_status', header: 'Fine Status', render: fineBadge }
  ],
  exportColumns: [
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'phone', header: 'Contact' },
    { key: 'book_title', header: 'Book' },
    { key: 'accession_no', header: 'Accession No.' },
    { key: 'issue_date', header: 'Issue Date', render: (r) => dateOnly(r.issue_date) },
    { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
    { key: 'overdue_days', header: 'Days Overdue' },
    { key: 'fine_amount', header: 'Fine Amount', render: (r) => money(r.fine_amount) },
    { key: 'fine_status', header: 'Fine Status', render: (r) => titleCase(r.fine_status) }
  ],
  fetchFn: (libraryId, params) => getOverdueBooksReport(libraryId, params)
};

// 5.6 Fine Collection Report
export const fineCollectionConfig = {
  key: 'fine-collection',
  category: 'Finance',
  title: 'Fine Collection Report',
  subtitle: 'Review fine assessments and collected amounts for a selected period.',
  icon: 'fas fa-hand-holding-usd',
  printTitle: 'Fine Collection Report',
  fileNamePrefix: 'Fine_Collection_Report',
  searchPlaceholder: 'Search member, book...',
  filters: [...dateFilters, { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions }],
  columns: [
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'book_title', header: 'Book' },
    { key: 'reason', header: 'Reason' },
    { key: 'amount', header: 'Amount', render: (r) => money(r.amount) },
    { key: 'payment_method', header: 'Mode' },
    { key: 'transaction_id', header: 'Reference' },
    { key: 'paid_at', header: 'Payment Date', render: (r) => dateOnly(r.paid_at) }
  ],
  exportColumns: [
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'book_title', header: 'Book' },
    { key: 'reason', header: 'Reason' },
    { key: 'amount', header: 'Amount', render: (r) => money(r.amount) },
    { key: 'payment_method', header: 'Payment Mode' },
    { key: 'transaction_id', header: 'Reference' },
    { key: 'paid_at', header: 'Payment Date', render: (r) => dateOnly(r.paid_at) }
  ],
  fetchFn: (libraryId, params) => getFineCollectionReport(libraryId, params),
  renderTotals: (totals) => (
    <>
      <span className="rpt-total-chip">Total Fine Generated<strong>{money(totals.totalGenerated)}</strong></span>
      <span className="rpt-total-chip">Total Collected<strong>{money(totals.totalCollected)}</strong></span>
    </>
  ),
  renderTotalsText: (totals) => (totals ? `Total Fine Generated: ${money(totals.totalGenerated)} | Total Collected: ${money(totals.totalCollected)}` : '')
};

// 5.6 Pending Fine Report
export const pendingFineConfig = {
  key: 'pending-fines',
  category: 'Finance',
  title: 'Pending Fine Report',
  subtitle: 'Review outstanding member fines and related circulation records.',
  icon: 'fas fa-coins',
  printTitle: 'Pending Fine Report',
  fileNamePrefix: 'Pending_Fine_Report',
  searchPlaceholder: 'Search member, book...',
  filters: [
    ...dateFilters,
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions },
    { key: 'fine_status', label: 'Fine Status', type: 'select', options: PENDING_FINE_STATUS_OPTIONS }
  ],
  columns: [
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'book_title', header: 'Book' },
    { key: 'reason', header: 'Reason' },
    { key: 'balance_amount', header: 'Pending Amount', render: (r) => money(r.balance_amount) },
    { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
    { key: 'days_overdue', header: 'Days Overdue' },
    { key: 'fine_status', header: 'Status', render: fineBadge }
  ],
  exportColumns: [
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'book_title', header: 'Book' },
    { key: 'reason', header: 'Reason' },
    { key: 'amount', header: 'Fine Amount', render: (r) => money(r.amount) },
    { key: 'balance_amount', header: 'Pending Amount', render: (r) => money(r.balance_amount) },
    { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
    { key: 'days_overdue', header: 'Days Overdue' },
    { key: 'fine_status', header: 'Status', render: (r) => titleCase(r.fine_status) }
  ],
  fetchFn: (libraryId, params) => getPendingFineReport(libraryId, params),
  renderTotals: (totals) => (
    <>
      <span className="rpt-total-chip">Total Fine Generated<strong>{money(totals.totalGenerated)}</strong></span>
      <span className="rpt-total-chip">Total Pending<strong>{money(totals.totalPending)}</strong></span>
    </>
  ),
  renderTotalsText: (totals) => (totals ? `Total Fine Generated: ${money(totals.totalGenerated)} | Total Pending: ${money(totals.totalPending)}` : '')
};

// 5.7 Daily Issue & Return Report — a genuinely different shape (two lists,
// not one paginated table), so it supplies its own renderBody instead of
// config.columns.
export const dailyIssueReturnConfig = {
  key: 'daily-issue-return',
  category: 'Circulation',
  title: 'Daily Issue & Return Report',
  subtitle: 'Review all issue and return activity for a selected day.',
  icon: 'fas fa-calendar-day',
  printTitle: 'Daily Issue & Return Report',
  fileNamePrefix: 'Daily_Issue_Return_Report',
  pdfOrientation: 'portrait',
  paginated: false,
  filters: [{ key: 'date', label: 'Date', type: 'date' }],
  // Not used for the on-screen body (custom renderBody below), but still
  // needed for CSV/Excel/PDF export column definitions.
  columns: [],
  exportColumns: [
    { key: 'section', header: 'Type' },
    { key: 'member_name', header: 'Member' },
    { key: 'book_title', header: 'Book' },
    { key: 'accession_no', header: 'Accession No.' },
    { key: 'issue_date', header: 'Issue Date', render: (r) => dateOnly(r.issue_date) },
    { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) },
    { key: 'return_date', header: 'Return Date', render: (r) => dateOnly(r.return_date) },
    { key: 'fine_amount', header: 'Fine', render: (r) => (r.fine_amount != null ? money(r.fine_amount) : '') }
  ],
  fetchFn: async (libraryId, params) => {
    const dateParam = params.date || new Date().toISOString().slice(0, 10);
    const res = await getDailyIssueReturnReport(libraryId, { ...params, date: dateParam });
    if (!res || res.success === false) return res;
    const issued = (res.data?.issued || []).map((r) => ({ ...r, section: 'Issued' }));
    const returned = (res.data?.returned || []).map((r) => ({ ...r, section: 'Returned' }));
    return { ...res, data: [...issued, ...returned], totals: res.data?.totals };
  },
  renderTotals: (totals) => (
    <>
      <span className="rpt-total-chip">Total Issued<strong>{totals.totalIssued}</strong></span>
      <span className="rpt-total-chip">Total Returned<strong>{totals.totalReturned}</strong></span>
    </>
  ),
  renderTotalsText: (totals) => (totals ? `Total Issued: ${totals.totalIssued} | Total Returned: ${totals.totalReturned}` : ''),
  renderBody: ({ data }) => {
    const issued = (data || []).filter((r) => r.section === 'Issued');
    const returned = (data || []).filter((r) => r.section === 'Returned');
    const table = (rows, columns, emptyText) => (
      <div className="table-responsive mb-4">
        <table className="table table-bordered table-hover align-middle lib-table-cell">
          <thead className="table-light lib-table-header">
            <tr>{columns.map((c) => <th key={c.key}>{c.header}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length > 0 ? rows.map((row) => (
              <tr key={row.id}>
                {columns.map((c) => <td key={c.key} data-label={c.header}>{c.render ? c.render(row) : row[c.key] ?? '-'}</td>)}
              </tr>
            )) : <tr><td colSpan={columns.length} className="text-center text-muted py-3">{emptyText}</td></tr>}
          </tbody>
        </table>
      </div>
    );

    const issuedColumns = [
      { key: 'member_name', header: 'Member' },
      { key: 'book_title', header: 'Book' },
      { key: 'accession_no', header: 'Accession No.' },
      { key: 'issue_date', header: 'Issue Time', render: (r) => dateOnly(r.issue_date) },
      { key: 'due_date', header: 'Due Date', render: (r) => dateOnly(r.due_date) }
    ];
    const returnedColumns = [
      { key: 'member_name', header: 'Member' },
      { key: 'book_title', header: 'Book' },
      { key: 'accession_no', header: 'Accession No.' },
      { key: 'return_date', header: 'Return Time', render: (r) => dateOnly(r.return_date) },
      { key: 'fine_amount', header: 'Fine', render: (r) => (r.fine_amount != null ? money(r.fine_amount) : '-') }
    ];

    return (
      <>
        <h6><i className="fas fa-arrow-circle-up me-1 text-primary" /> Issues</h6>
        {table(issued, issuedColumns, 'No books issued on this date')}
        <h6><i className="fas fa-arrow-circle-down me-1 text-success" /> Returns</h6>
        {table(returned, returnedColumns, 'No books returned on this date')}
      </>
    );
  }
};

// Options loader for the category filter below — same optionsLoader
// convention as the member-category filters (getMemberCategoryOptions)
// elsewhere in this file, just library-scoped, so ReportPage passes
// libraryId through to it (see ReportPage.jsx's optionsLoader effect).
const loadCategoryOptionsForLibrary = async (libraryId) => {
  if (!libraryId) return [];
  const categories = await getBookCategoriesByLibrary(libraryId);
  return (categories || []).map((c) => ({ value: c.id, label: c.name }));
};

// 5.8 Most Issued Books Report
export const mostIssuedBooksConfig = {
  key: 'most-issued-books',
  category: 'Books',
  title: 'Most Issued Books Report',
  subtitle: 'Identify books with the highest circulation within a selected period.',
  icon: 'fas fa-fire',
  printTitle: 'Most Issued Books Report',
  fileNamePrefix: 'Most_Issued_Books_Report',
  pdfOrientation: 'portrait',
  paginated: false,
  filters: [
    ...dateFilters,
    { key: 'author_name', label: 'Author', type: 'text' },
    { key: 'category_id', label: 'Category', type: 'select', options: [], optionsLoader: loadCategoryOptionsForLibrary },
    { key: 'top', label: 'Show Top', type: 'select', options: [
      { value: '10', label: 'Top 10' },
      { value: '20', label: 'Top 20' },
      { value: '50', label: 'Top 50' },
      { value: '200', label: 'All' }
    ] }
  ],
  columns: [
    { key: 'rank', header: 'Rank' },
    { key: 'book_title', header: 'Book' },
    { key: 'author_name', header: 'Author' },
    { key: 'category_name', header: 'Category' },
    { key: 'isbn', header: 'ISBN' },
    { key: 'issue_count', header: 'Total Issues' },
    { key: 'available_copies', header: 'Available / Total', render: (r) => `${r.available_copies} / ${r.total_copies}` }
  ],
  exportColumns: [
    { key: 'rank', header: 'Rank' },
    { key: 'book_title', header: 'Book' },
    { key: 'author_name', header: 'Author' },
    { key: 'category_name', header: 'Category' },
    { key: 'isbn', header: 'ISBN' },
    { key: 'issue_count', header: 'Total Issues' },
    { key: 'available_copies', header: 'Available Copies' },
    { key: 'total_copies', header: 'Total Copies' }
  ],
  fetchFn: async (libraryId, params) => {
    const res = await getMostIssuedBooksReport(libraryId, params);
    return res;
  }
};

// 5.9 Most Active Members Report
export const mostActiveMembersConfig = {
  key: 'most-active-members',
  category: 'Members',
  title: 'Most Active Members Report',
  subtitle: 'Rank members by valid borrowing activity within a selected period.',
  icon: 'fas fa-user-check',
  printTitle: 'Most Active Members Report',
  fileNamePrefix: 'Most_Active_Members_Report',
  pdfOrientation: 'portrait',
  paginated: false,
  filters: [
    ...dateFilters,
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions },
    { key: 'top', label: 'Show Top', type: 'select', options: [
      { value: '10', label: 'Top 10' },
      { value: '20', label: 'Top 20' },
      { value: '50', label: 'Top 50' },
      { value: '200', label: 'All' }
    ] }
  ],
  columns: [
    { key: 'rank', header: 'Rank' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_name', header: 'Member' },
    { key: 'member_source', header: 'Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'books_issued', header: 'Issued' },
    { key: 'books_returned', header: 'Returned' },
    { key: 'currently_issued', header: 'Current' },
    { key: 'overdue_books', header: 'Overdue' },
    { key: 'fine_amount', header: 'Fine', render: (r) => money(r.fine_amount) }
  ],
  exportColumns: [
    { key: 'rank', header: 'Rank' },
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'books_issued', header: 'Books Issued' },
    { key: 'books_returned', header: 'Books Returned' },
    { key: 'currently_issued', header: 'Currently Issued' },
    { key: 'overdue_books', header: 'Overdue Books' },
    { key: 'fine_amount', header: 'Fine Amount', render: (r) => money(r.fine_amount) }
  ],
  fetchFn: (libraryId, params) => getMostActiveMembersReport(libraryId, params)
};

// 5.10 Lost/Damaged Books Report
export const lostDamagedConfig = {
  key: 'lost-damaged',
  category: 'Books',
  title: 'Lost / Damaged Books Report',
  subtitle: 'Review copies recorded as lost or damaged and related issue details.',
  icon: 'fas fa-band-aid',
  printTitle: 'Lost/Damaged Books Report',
  fileNamePrefix: 'Lost_Damaged_Books_Report',
  searchPlaceholder: 'Search book, accession no, member...',
  filters: [
    ...dateFilters,
    { key: 'status', label: 'Status', type: 'select', options: [
      { value: 'lost', label: 'Lost' },
      { value: 'damaged', label: 'Damaged' }
    ] },
    { key: 'fine_status', label: 'Recovery Status', type: 'select', options: FINE_STATUS_OPTIONS }
  ],
  columns: [
    { key: 'book_title', header: 'Book' },
    { key: 'accession_no', header: 'Accession No.' },
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'availability_status', header: 'Status', render: (r) => (
      <span className={`badge ${r.availability_status === 'lost' ? 'bg-danger' : 'bg-warning text-dark'}`}>{titleCase(r.availability_status)}</span>
    ) },
    { key: 'reported_date', header: 'Reported Date', render: (r) => dateOnly(r.reported_date) },
    { key: 'fine_amount', header: 'Fine/Replacement', render: (r) => (r.fine_amount != null ? money(r.fine_amount) : '-') },
    { key: 'fine_status', header: 'Recovery Status', render: fineBadge }
  ],
  exportColumns: [
    { key: 'book_title', header: 'Book' },
    { key: 'accession_no', header: 'Accession No.' },
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'availability_status', header: 'Status', render: (r) => titleCase(r.availability_status) },
    { key: 'reported_date', header: 'Reported Date', render: (r) => dateOnly(r.reported_date) },
    { key: 'fine_amount', header: 'Fine/Replacement Amount', render: (r) => money(r.fine_amount) },
    { key: 'fine_status', header: 'Recovery Status', render: (r) => titleCase(r.fine_status) },
    { key: 'copy_remarks', header: 'Remarks' }
  ],
  fetchFn: (libraryId, params) => getLostDamagedReport(libraryId, params)
};

// 5.11 Reservation/Hold Report — reservation functionality is confirmed
// real and functional (reservationModel/reservationController), so this
// report is fully implemented, not marked N/A.
export const reservationReportConfig = {
  key: 'reservation-report',
  category: 'Reservations',
  title: 'Reservation / Hold Report',
  subtitle: 'Review active, completed, cancelled and expired reservations.',
  icon: 'fas fa-calendar-check',
  printTitle: 'Reservation/Hold Report',
  fileNamePrefix: 'Reservation_Hold_Report',
  searchPlaceholder: 'Search member, book...',
  filters: [
    ...dateFilters,
    { key: 'member_source', label: 'Member Category', type: 'select', options: MEMBER_CATEGORY_OPTIONS, optionsLoader: getMemberCategoryOptions },
    { key: 'status', label: 'Status', type: 'select', options: RESERVATION_STATUS_OPTIONS }
  ],
  columns: [
    { key: 'id', header: 'Reservation ID' },
    { key: 'member_name', header: 'Member' },
    { key: 'member_source', header: 'Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'book_title', header: 'Book' },
    { key: 'requested_at', header: 'Reserved On', render: (r) => dateOnly(r.requested_at) },
    { key: 'status', header: 'Status', render: (r) => <span className="badge bg-info text-dark">{titleCase(r.status)}</span> },
    { key: 'expires_at', header: 'Expiry', render: (r) => dateOnly(r.expires_at) }
  ],
  exportColumns: [
    { key: 'id', header: 'Reservation ID' },
    { key: 'member_name', header: 'Member' },
    { key: 'membership_no', header: 'Membership No.' },
    { key: 'member_source', header: 'Member Category', render: (r) => memberSourceLabel(r.member_source) },
    { key: 'book_title', header: 'Book' },
    { key: 'requested_at', header: 'Reservation Date', render: (r) => dateOnly(r.requested_at) },
    { key: 'status', header: 'Status', render: (r) => titleCase(r.status) },
    { key: 'expires_at', header: 'Expiry Date', render: (r) => dateOnly(r.expires_at) }
  ],
  fetchFn: (libraryId, params) => getReservationReportData(libraryId, params)
};

// Category → cards, used by ReportsHub.jsx. Order matches the request's
// suggested landing-page structure.
export const REPORT_CATEGORIES = [
  { name: 'Circulation', reports: [memberIssueHistoryConfig, bookIssueHistoryConfig, issuesReportConfig, dailyIssueReturnConfig, overdueBooksConfig] },
  { name: 'Members', reports: [issuesByMemberCategoryConfig, mostActiveMembersConfig] },
  { name: 'Books', reports: [mostIssuedBooksConfig, lostDamagedConfig] },
  { name: 'Finance', reports: [fineCollectionConfig, pendingFineConfig] },
  { name: 'Reservations', reports: [reservationReportConfig] }
];

export const ALL_REPORT_CONFIGS = REPORT_CATEGORIES.flatMap((c) => c.reports);
